import { useEffect, useState } from 'react';
import type { Channel } from '@huddle/shared';
import { get } from '../lib/api';
import { setAppBadge } from '../lib/push';

/**
 * Unread totals for the tab bar and the app icon badge.
 *
 * Deliberately a tiny polling store rather than a global socket: the channel
 * list is not worth holding a WebSocket open for, and one lightweight request
 * a minute is cheaper on battery than a persistent connection the app does not
 * otherwise need. The channel you are *in* is realtime; the badge is not.
 */

/**
 * 'loading' is not the same as 'no channels'.
 *
 * The cache used to start as an empty array with failures swallowed, so the
 * home screen had no loading state at all: every cold launch showed
 * "No channels yet." until the request landed, and showed it forever when the
 * phone had no signal. That is the first thing a new hire sees.
 */
export type ListStatus = 'loading' | 'ready' | 'error';

/**
 * Where the list lands after an attempt.
 *
 * Pulled out so the rule is testable on its own, the same way
 * `nextStateOnError` is in state/session.tsx — and for the same reason: only a
 * cold start can fail into an error. Once a list is on screen, a failed poll
 * must leave it alone rather than replacing a working screen with an error or,
 * as before, with "No channels yet."
 */
export function nextListStatus(current: ListStatus, ok: boolean): ListStatus {
  if (ok) return 'ready';
  return current === 'loading' ? 'error' : current;
}

let cache: Channel[] = [];
let status: ListStatus = 'loading';
const listeners = new Set<(channels: Channel[], status: ListStatus) => void>();
let timer: number | null = null;
let refreshing = false;

function notify(): void {
  for (const listener of listeners) listener(cache, status);
}

async function refresh(): Promise<void> {
  if (refreshing) return;
  refreshing = true;
  try {
    cache = await get<Channel[]>('/channels');
    status = nextListStatus(status, true);
    notify();
    setAppBadge(cache.reduce((n, c) => n + c.unreadCount, 0));
  } catch {
    // Offline. Keep whatever we last had rather than zeroing the badges.
    const next = nextListStatus(status, false);
    if (next !== status) {
      status = next;
      notify();
    }
  } finally {
    refreshing = false;
  }
}

export function refreshChannels(): void {
  void refresh();
}

function start(): void {
  if (timer !== null) return;
  void refresh();
  timer = window.setInterval(() => {
    // No point polling a backgrounded app; iOS throttles it anyway and it
    // just burns battery in an apron pocket.
    if (document.visibilityState === 'visible') void refresh();
  }, 60_000);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refresh();
  });
  window.addEventListener('online', () => void refresh());
}

export function useChannels(): {
  channels: Channel[];
  status: ListStatus;
  reload: () => void;
} {
  const [snapshot, setSnapshot] = useState<{ channels: Channel[]; status: ListStatus }>(() => ({
    channels: cache,
    status,
  }));

  useEffect(() => {
    const listener = (channels: Channel[], next: ListStatus) =>
      setSnapshot({ channels, status: next });
    listeners.add(listener);
    // A second mount after the store has already loaded would otherwise sit on
    // whatever this component captured on its first render.
    setSnapshot({ channels: cache, status });
    start();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return { channels: snapshot.channels, status: snapshot.status, reload: refreshChannels };
}

export function useUnread(): { totalUnread: number; totalMentions: number } {
  const { channels } = useChannels();
  return {
    totalUnread: channels.reduce((n, c) => n + c.unreadCount, 0),
    totalMentions: channels.reduce((n, c) => n + c.mentionCount, 0),
  };
}
